import { useState, useEffect, useCallback, useRef } from 'react';
import { versionControlService, VersionConflictError, ActiveEditor } from '../services/versionControlService';
import { validationService } from '../services/validationService';
import { useAuth } from '../context/AuthContext';

interface UseVersionControlOptions<T> {
  tableName: string;
  recordId: string | null;
  onConflict?: (conflict: VersionConflictError) => void;
  onSaveSuccess?: (data: T) => void;
  autoRenewLock?: boolean;
}

export function useVersionControl<T extends { id: string; version: number }>(
  options: UseVersionControlOptions<T>
) {
  const { user, organization, orgMember } = useAuth();
  const [activeEditors, setActiveEditors] = useState<ActiveEditor[]>([]);
  const [hasLock, setHasLock] = useState(false);
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string }>>([]);
  const lockRenewalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { tableName, recordId, onConflict, onSaveSuccess, autoRenewLock = true } = options;

  useEffect(() => {
    if (recordId) {
      loadActiveEditors();
      const interval = setInterval(loadActiveEditors, 10000);
      return () => clearInterval(interval);
    }
  }, [recordId]);

  useEffect(() => {
    return () => {
      if (hasLock && recordId && user) {
        versionControlService.releaseEditLock(tableName, recordId, user.id);
      }
      if (lockRenewalRef.current) {
        clearInterval(lockRenewalRef.current);
      }
    };
  }, [hasLock, recordId, user, tableName]);

  const loadActiveEditors = async () => {
    if (!recordId) return;
    const editors = await versionControlService.getActiveEditors(tableName, recordId);
    setActiveEditors(editors);
  };

  const acquireLock = async (): Promise<boolean> => {
    if (!recordId || !user || !organization) return false;

    const result = await versionControlService.acquireEditLock(
      tableName,
      recordId,
      user.id,
      orgMember?.display_name || user.email || 'Unknown User',
      organization.id
    );

    if (result.success) {
      setHasLock(true);

      if (autoRenewLock && lockRenewalRef.current === null) {
        lockRenewalRef.current = setInterval(() => {
          if (recordId && user) {
            versionControlService.renewEditLock(tableName, recordId, user.id);
          }
        }, 2 * 60 * 1000);
      }

      return true;
    }

    return false;
  };

  const releaseLock = async () => {
    if (!recordId || !user) return;

    await versionControlService.releaseEditLock(tableName, recordId, user.id);
    setHasLock(false);

    if (lockRenewalRef.current) {
      clearInterval(lockRenewalRef.current);
      lockRenewalRef.current = null;
    }
  };

  const validateRecord = async (record: Record<string, any>): Promise<boolean> => {
    const result = await validationService.validate(tableName, record);
    setValidationErrors(result.errors);
    return result.valid;
  };

  const saveWithVersionCheck = async (
    record: T,
    updates: Partial<T>,
    changeSummary?: string
  ): Promise<{ success: boolean; data?: T; conflict?: VersionConflictError }> => {
    if (!user) {
      return { success: false };
    }

    const isValid = await validateRecord({ ...record, ...updates });
    if (!isValid) {
      return { success: false };
    }

    const result = await versionControlService.updateWithVersionCheck(
      tableName,
      record,
      updates,
      user.id,
      changeSummary
    );

    if (result.conflict) {
      setConflict(result.conflict);
      if (onConflict) {
        onConflict(result.conflict);
      }
      return { success: false, conflict: result.conflict };
    }

    if (result.data) {
      if (onSaveSuccess) {
        onSaveSuccess(result.data);
      }
      setValidationErrors([]);
      return { success: true, data: result.data };
    }

    return { success: false };
  };

  const clearConflict = useCallback(() => {
    setConflict(null);
  }, []);

  const recordHistory = async (params: {
    recordId: string;
    operation: 'insert' | 'update' | 'delete';
    oldValues?: any;
    newValues?: any;
    changeSummary?: string;
  }) => {
    if (!user || !organization) return;

    await versionControlService.recordVersionHistory({
      tableName,
      recordId: params.recordId,
      operation: params.operation,
      oldValues: params.oldValues,
      newValues: params.newValues,
      changedBy: user.id,
      changeSummary: params.changeSummary,
      orgId: organization.id,
    });
  };

  return {
    activeEditors,
    hasLock,
    conflict,
    validationErrors,
    acquireLock,
    releaseLock,
    saveWithVersionCheck,
    validateRecord,
    clearConflict,
    recordHistory,
    loadActiveEditors,
  };
}
